import React, { useEffect, useState } from "react";
import { useToastStore, type ToastType } from "store/toast";

const DEFAULT_ICONS: Record<ToastType, string> = {
	success: "✓",
	error: "✕",
	info: "ℹ",
	warning: "⚠",
};

export const Toast: React.FC = () => {
	const { toastState, hideToast } = useToastStore();
	const [isVisible, setIsVisible] = useState(false);
	const [isPaused, setIsPaused] = useState(false);

	const handleClose = React.useCallback(() => {
		setIsVisible(false);
		setTimeout(hideToast, 300);
	}, [hideToast]);

	useEffect(() => {
		if (toastState.show) {
			setIsVisible(true);

			if (toastState.duration > 0 && !isPaused) {
				const timer = setTimeout(() => {
					handleClose();
				}, toastState.duration);

				return () => clearTimeout(timer);
			}
		} else {
			setIsVisible(false);
		}
	}, [toastState.show, toastState.duration, isPaused, handleClose]);

	const handleMouseEnter = () => {
		if (toastState.pauseOnHover) {
			setIsPaused(true);
		}
	};

	const handleMouseLeave = () => {
		if (toastState.pauseOnHover) {
			setIsPaused(false);
		}
	};

	const handleButtonClick = (onClick: () => void) => {
		onClick();
		handleClose();
	};

	if (!toastState.show && !isVisible) {
		return null;
	}

	const displayIcon = toastState.customIcon || DEFAULT_ICONS[toastState.type];

	const toastClasses = [
		"custom-toast",
		`custom-toast--${toastState.type}`,
		`custom-toast--${toastState.position}`,
		isVisible ? "custom-toast--visible" : "custom-toast--hidden",
		toastState.customClassName,
	]
		.filter(Boolean)
		.join(" ");

	return (
		<div
			className={toastClasses}
			style={toastState.style}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
		>
			{displayIcon && <span className="toast-icon">{displayIcon}</span>}
			<div className="toast-content">
				<span className="toast-message">{toastState.message}</span>
				{toastState.buttons && toastState.buttons.length > 0 && (
					<div className="toast-buttons">
						{toastState.buttons.map((button, index) => (
							<button
								key={index}
								className={`toast-button toast-button--${
									button.variant || "primary"
								} ${button.className || ""}`}
								onClick={() => handleButtonClick(button.onClick)}
								type="button"
							>
								{button.label}
							</button>
						))}
					</div>
				)}
			</div>
			{toastState.showCloseButton && (
				<button
					className="toast-close-button"
					onClick={handleClose}
					type="button"
					aria-label="Close"
				>
					×
				</button>
			)}
		</div>
	);
};
export default Toast;
